/**
 * Global UI state.
 *
 * Deliberately small: the selected project, the time range and the
 * aggregation - all things every chart reads and the header changes.
 * Everything else that looks like state is server state and belongs to
 * TanStack Query, not here.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

import { RELATIVE_UNITS, relativeWindow, type RelativeUnit } from "./timeRange";

export type { RelativeUnit } from "./timeRange";

export type RangeKey = "1D" | "1W" | "1M" | "1Y" | "ALL" | "CUSTOM" | "RELATIVE";

/** "Previous `count` `unit`, ending `fromCount` `fromUnit` ago." */
export interface RelativeSpec {
  count: number;
  unit: RelativeUnit;
  fromCount: number;
  fromUnit: RelativeUnit;
}

export interface TimeRange {
  key: RangeKey;
  start: Date;
  end: Date;
  /** Present for RELATIVE, so the window can be recomputed from "now". */
  relative?: RelativeSpec;
}

export type AggInterval = "none" | "hour" | "day" | "week" | "month" | "custom";
export type AggMethod = "max" | "min" | "sum" | "mean" | "count" | "greatest" | "peak-to-peak";

export interface Aggregation {
  /** Auto picks the interval from the range; manual uses the fields below. */
  mode: "auto" | "manual";
  interval: AggInterval;
  /** Bucket width when interval is "custom". */
  customSeconds: number;
  method: AggMethod;
  /** A trailing window at every sample, instead of fixed buckets. */
  rolling: boolean;
}

export const DEFAULT_AGGREGATION: Aggregation = {
  mode: "auto",
  interval: "day",
  customSeconds: 900,
  method: "greatest",
  rolling: false,
};

const RANGE_DAYS: Record<"1D" | "1W" | "1M" | "1Y", number> = {
  "1D": 1,
  "1W": 7,
  "1M": 30,
  "1Y": 365,
};

export function rangeFor(key: RangeKey): { start: Date; end: Date } {
  const end = new Date();
  if (key === "ALL") {
    // Far enough back to cover any commissioned structure without pretending
    // to be unbounded, which would break the automatic interval choice.
    return { start: new Date(end.getTime() - 3650 * 86400_000), end };
  }
  if (key === "CUSTOM" || key === "RELATIVE") {
    return { start: new Date(end.getTime() - 30 * 86400_000), end };
  }
  return { start: new Date(end.getTime() - RANGE_DAYS[key] * 86400_000), end };
}

interface UiState {
  projectCode: string | null;
  range: TimeRange;
  aggregation: Aggregation;
  /** Device codes to show, per project. Absent means every device. */
  deviceFilters: Record<string, string[]>;
  sidebarOpen: boolean;

  setProject: (code: string | null) => void;
  setRangeKey: (key: RangeKey) => void;
  setCustomRange: (start: Date, end: Date) => void;
  setRelativeRange: (spec: RelativeSpec) => void;
  setAggregation: (aggregation: Aggregation) => void;
  /** Null or an empty list clears the filter for that project. */
  setDeviceFilter: (projectCode: string, devices: string[] | null) => void;
  toggleSidebar: () => void;
}

const METHODS: AggMethod[] = ["max", "min", "sum", "mean", "count", "greatest", "peak-to-peak"];
const INTERVALS: AggInterval[] = ["none", "hour", "day", "week", "month", "custom"];

/**
 * Accept only a well-formed stored aggregation.
 *
 * This key used to hold a bare string ("auto", "1h"...). A browser that last
 * visited before that changed still has one, and spreading it into an object
 * would produce an aggregation with no method - so anything unrecognised
 * falls back to the default.
 */
function restoreAggregation(value: unknown): Aggregation {
  if (!value || typeof value !== "object") return DEFAULT_AGGREGATION;
  const v = value as Partial<Aggregation>;
  return {
    mode: v.mode === "manual" ? "manual" : "auto",
    interval: INTERVALS.includes(v.interval as AggInterval) ? (v.interval as AggInterval) : "day",
    customSeconds:
      typeof v.customSeconds === "number" && v.customSeconds >= 60 ? v.customSeconds : 900,
    method: METHODS.includes(v.method as AggMethod) ? (v.method as AggMethod) : "greatest",
    rolling: v.rolling === true,
  };
}

function isRelativeSpec(value: unknown): value is RelativeSpec {
  const v = value as Partial<RelativeSpec> | undefined;
  return Boolean(
    v &&
      Number.isFinite(v.count) &&
      Number.isFinite(v.fromCount) &&
      RELATIVE_UNITS.includes(v.unit as RelativeUnit) &&
      RELATIVE_UNITS.includes(v.fromUnit as RelativeUnit),
  );
}

interface Persisted {
  projectCode?: string | null;
  aggregation?: unknown;
  sidebarOpen?: boolean;
  rangeKey?: RangeKey;
  customStart?: string;
  customEnd?: string;
  relative?: unknown;
  deviceFilters?: unknown;
}

function restoreDeviceFilters(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== "object") return {};
  const out: Record<string, string[]> = {};
  for (const [project, codes] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(codes)) {
      const valid = codes.filter((c): c is string => typeof c === "string");
      if (valid.length) out[project] = valid;
    }
  }
  return out;
}

function restoreRange(p: Persisted): TimeRange {
  if (p.rangeKey === "CUSTOM" && p.customStart && p.customEnd) {
    const start = new Date(p.customStart);
    const end = new Date(p.customEnd);
    if (!Number.isNaN(start.getTime()) && end > start) return { key: "CUSTOM", start, end };
  }
  // A relative range is stored as its definition and re-evaluated, so "the
  // previous 7 days" still means the previous 7 days on tomorrow's visit.
  if (p.rangeKey === "RELATIVE" && isRelativeSpec(p.relative)) {
    return { key: "RELATIVE", ...relativeWindow(p.relative), relative: p.relative };
  }
  const key: RangeKey =
    p.rangeKey && ["1D", "1W", "1M", "1Y", "ALL"].includes(p.rangeKey) ? p.rangeKey : "1M";
  return { key, ...rangeFor(key) };
}

const initialRange = rangeFor("1M");

export const useUi = create<UiState>()(
  persist(
    (set, get) => ({
      projectCode: null,
      range: { key: "1M", ...initialRange },
      aggregation: DEFAULT_AGGREGATION,
      deviceFilters: {},
      sidebarOpen: true,

      setProject: (code) => set({ projectCode: code }),

      setRangeKey: (key) => {
        const current = get().range;
        if (key === "CUSTOM") {
          set({ range: { key: "CUSTOM", start: current.start, end: current.end } });
          return;
        }
        if (key === "RELATIVE" && current.relative) {
          set({
            range: { key, ...relativeWindow(current.relative), relative: current.relative },
          });
          return;
        }
        set({ range: { key, ...rangeFor(key) } });
      },

      setCustomRange: (start, end) => set({ range: { key: "CUSTOM", start, end } }),

      setRelativeRange: (relative) =>
        set({ range: { key: "RELATIVE", ...relativeWindow(relative), relative } }),

      setAggregation: (aggregation) => set({ aggregation }),

      setDeviceFilter: (projectCode, devices) =>
        set((s) => {
          const next = { ...s.deviceFilters };
          if (devices && devices.length) next[projectCode] = devices;
          else delete next[projectCode];
          return { deviceFilters: next };
        }),

      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
    }),
    {
      name: "kinetix.ui",
      // Dates do not survive JSON. Preset ranges are stored as their key and
      // recomputed from now; a custom range keeps its absolute ends; a relative
      // one keeps its definition.
      partialize: (s): Persisted => ({
        projectCode: s.projectCode,
        aggregation: s.aggregation,
        sidebarOpen: s.sidebarOpen,
        rangeKey: s.range.key,
        customStart: s.range.key === "CUSTOM" ? s.range.start.toISOString() : undefined,
        customEnd: s.range.key === "CUSTOM" ? s.range.end.toISOString() : undefined,
        relative: s.range.relative,
        deviceFilters: s.deviceFilters,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Persisted;
        return {
          ...current,
          projectCode: p.projectCode ?? current.projectCode,
          sidebarOpen: p.sidebarOpen ?? current.sidebarOpen,
          aggregation: restoreAggregation(p.aggregation),
          range: restoreRange(p),
          deviceFilters: restoreDeviceFilters(p.deviceFilters),
        };
      },
    },
  ),
);
